import { Box, useBreakpointValue } from '@hover/blueprint';
import React, { useState } from 'react';

import { Meta } from 'src/modules/app/components/Meta/Meta';
import { FullscreenImage } from 'src/modules/homeownerApp/components/FullscreenImage';
import { Header } from 'src/modules/homeownerApp/components/SharePageLayout/Header';
import {
  PartnerOriginalImageProvider,
  usePartnerCobranding,
} from 'src/modules/homeownerApp/hooks/usePartner';
import { PageContainer } from 'src/shared/components/Layout/PageContainer';
import type { AiDisplayImage } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { generatePath } from 'src/shared/route';
import { makeUrl } from 'src/shared/utils/url';

import { HomeownerAppProvider } from '../../providers';

import type { DetailProps } from './Details';
import { Details } from './Details';
import { Images } from './Images';
import { MobileImages } from './MobileImages';
import { OriginalImages } from './OriginalImages';

interface ShareLayoutProps {
  shareId: string;
  images: AiDisplayImage[];
  trackingData?: Record<string, unknown>;
  fullscreenImagePath: string;
  fullscreenReturnPath: string;
  details: DetailProps;
  projectOrgId?: number;
}

export const SharePageLayout = ({
  shareId: id,
  images,
  trackingData,
  fullscreenImagePath,
  fullscreenReturnPath,
  details,
  projectOrgId: orgId,
}: ShareLayoutProps) => {
  const [visibleIndex, setVisibleIndex] = useState<number>(0);
  const ImageList = useBreakpointValue(
    {
      base: MobileImages,
      tablet: Images,
    },
    { fallback: 'tablet' },
  );

  const firstAiGeneratedImageUrl =
    images?.[0]?.aiGeneratedImage.generatedImagePath;
  const aiOriginalImage = images?.[0]?.aiOriginalImage;

  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const partner = usePartnerCobranding();
  const title = partner?.displayName
    ? t('share.designedWithCobrand', { orgName: partner.displayName })
    : t('share.designedWith');

  // TODO MEOW-6666 - Move CTA logic to the frontend,
  // pass the affiliate org in graphql and not the path
  const { ctaPath } = details;
  const ctaUrl = new URL(makeUrl(ctaPath));
  const ctaSearchParams = new URLSearchParams(ctaUrl.search);
  const affOrgId = ctaSearchParams.get('aff_org_id');

  return (
    <HomeownerAppProvider
      displayIntercom={false}
      trackingDataOverrides={trackingData}
    >
      <PartnerOriginalImageProvider aiOriginalImage={aiOriginalImage}>
        <Meta
          description={t('share.shareDescription')}
          imageUrl={firstAiGeneratedImageUrl}
          title={title}
        />
        <Header affOrgId={affOrgId} />
        <PageContainer>
          <Box alignSelf="center" flexDirection="column" margin="auto">
            <Box
              flexDirection={{ base: 'column', tablet: 'row' }}
              gap={{ base: 700, tablet: 500, width500: 900 }}
              marginTop={{ base: 200, tablet: 500 }}
            >
              <Details {...details} />
              {!!images.length && (
                <>
                  <OriginalImages
                    aiDisplayImages={images}
                    display={{ base: 'none', tablet: 'inherit' }}
                    visibleIndex={visibleIndex}
                  />
                  <FullscreenImage
                    images={images}
                    isUnauthenticated
                    path={generatePath(fullscreenImagePath, {
                      id,
                      aiDisplayImageId: ':aiDisplayImageId',
                    })}
                    returnPath={generatePath(fullscreenReturnPath, { id })}
                  >
                    <ImageList
                      aiDisplayImages={images}
                      fullScreenPath={fullscreenImagePath}
                      orgId={orgId}
                      setVisibleIndex={setVisibleIndex}
                      visibleIndex={visibleIndex}
                    />
                  </FullscreenImage>
                </>
              )}
            </Box>
          </Box>
        </PageContainer>
      </PartnerOriginalImageProvider>
    </HomeownerAppProvider>
  );
};
